import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { labelApi } from '../../shared/api/endpoints';
import './LabelCards.css';

const FALLBACKS = [
  'linear-gradient(155deg, #3a1058, #c2187a 60%, #c9a227)',
  'linear-gradient(155deg, #1a1224, #5c3d8f 55%, #e85d8a)',
  'linear-gradient(155deg, #2d0a0f, #8b2252 50%, #ff7e5f)',
  'linear-gradient(155deg, #0f2a28, #2a9d8f 50%, #e8c547)',
];

/** Pixels per second — slow continuous drift */
const SPEED = 26;
const CARD = 300;
const GAP = 15;
const STEP = CARD + GAP;

export default function LabelCards() {
  const [labels, setLabels] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [active, setActive] = useState(0);
  const trackRef = useRef(null);
  const posRef = useRef(0);
  const rafRef = useRef(0);
  const lastRef = useRef(0);
  const pausedRef = useRef(false);

  useEffect(() => {
    labelApi
      .list()
      .then((res) => setLabels(res.data.data || []))
      .catch(() => setLabels([]))
      .finally(() => setLoaded(true));
  }, []);

  const n = labels.length;
  const slides = useMemo(() => {
    if (!n) return [];
    return [...labels, ...labels];
  }, [labels, n]);

  const loopWidth = n * STEP;

  useEffect(() => {
    if (!n || !loopWidth) return undefined;

    const tick = (now) => {
      if (!lastRef.current) lastRef.current = now;
      const dt = Math.min(40, now - lastRef.current) / 1000;
      lastRef.current = now;

      if (!pausedRef.current) {
        posRef.current += SPEED * dt;
        if (posRef.current >= loopWidth) {
          posRef.current -= loopWidth;
        }
        const el = trackRef.current;
        if (el) {
          el.style.transform = `translate3d(${-posRef.current}px, 0, 0)`;
          const stage = el.parentElement;
          if (stage) {
            const mid = posRef.current + stage.clientWidth / 2;
            const idx = Math.floor(mid / STEP) % n;
            setActive((a) => (a === idx ? a : idx));
          }
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    lastRef.current = 0;
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [n, loopWidth]);

  const setPaused = (v) => {
    pausedRef.current = v;
    if (!v) lastRef.current = 0;
  };

  if (!loaded || n === 0) return null;

  return (
    <section className="section labels section--dark">
      <div className="labels__glow" aria-hidden />
      <div className="container labels__head">
        <div>
          <p className="section-eyebrow">Collections</p>
          <h2 className="section-title">Curated Worlds</h2>
          <p className="section-sub">A runway of edits — each world takes center stage.</p>
        </div>
        <div className="labels__controls">
          <span className="labels__pager">
            {String(active + 1).padStart(2, '0')}
            <i>/</i>
            {String(n).padStart(2, '0')}
          </span>
        </div>
      </div>

      <div
        className="labels__stage"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div className="labels__track" ref={trackRef}>
          {slides.map((label, i) => {
            const logical = i % n;
            const isOn = logical === active;
            return (
              <article
                key={`${label._id}-${i}`}
                className={`labels__card${isOn ? ' is-active' : ''}`}
              >
                <Link to={`/products?label=${label._id}`} className="labels__link">
                  <span
                    className="labels__media"
                    style={
                      label.image
                        ? { backgroundImage: `url(${label.image})` }
                        : { background: FALLBACKS[logical % FALLBACKS.length] }
                    }
                  />
                  <span className="labels__frame" aria-hidden />
                  <span className="labels__shine" aria-hidden />
                  <span className="labels__plate">
                    <em>The Edit</em>
                    <h3>{label.name}</h3>
                    {label.description && <p>{label.description}</p>}
                    <span className="labels__cta">Explore collection</span>
                  </span>
                </Link>
              </article>
            );
          })}
        </div>
      </div>

      <div className="labels__dots" role="tablist" aria-label="Collections">
        {labels.map((l, i) => (
          <button
            key={l._id}
            type="button"
            role="tab"
            aria-selected={i === active}
            className={i === active ? 'is-on' : ''}
            aria-label={l.name}
            onClick={() => {
              posRef.current = i * STEP;
              const el = trackRef.current;
              if (el) el.style.transform = `translate3d(${-posRef.current}px, 0, 0)`;
              setActive(i);
            }}
          />
        ))}
      </div>
    </section>
  );
}
